import { useState } from 'react'
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import useAuthStore from '../store/authStore'
import useFeaturesStore from '../store/featuresStore'
import PasswordInput from '../components/PasswordInput'
import Button from '../components/Button'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuthStore()
  const resetFeatures = useFeaturesStore((s) => s.reset)
  const loadFeatures = useFeaturesStore((s) => s.load)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const banner = location.state?.message

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(username, password)
      // Ricarica i feature flag per il nuovo utente/sessione: il modulo
      // controlling potrebbe essere abilitato e va riflesso subito senza
      // dover fare un hard reload.
      resetFeatures()
      await loadFeatures()
      navigate('/dashboard')
    } catch (err) {
      let msg = err.response?.data?.detail
      if (!msg) {
        if (err.response?.status === 429) {
          msg = 'Troppi tentativi falliti. Riprova tra qualche minuto.'
        } else if (err.response) {
          msg = 'Credenziali non valide'
        } else {
          msg = 'Impossibile contattare il server. Riprova.'
        }
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-brand-100 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Venetiae Gestionale</h1>
        <p className="text-gray-600 mb-6">Accedi al tuo account</p>

        {banner && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-md text-sm mb-4">
            {banner}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'Accesso in corso...' : 'Accedi'}
          </Button>

          <Link
            to="/forgot-password"
            className="block text-center text-sm text-blue-600 hover:underline"
          >
            Password dimenticata?
          </Link>
        </form>

        <div className="mt-6 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
          <Link to="/privacy" className="hover:text-gray-600 hover:underline">
            Informativa sulla privacy
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:text-gray-600 hover:underline">
            Termini di servizio
          </Link>
        </div>
      </div>
    </div>
  )
}
